import React, { useEffect } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { loginRequest } from '../../auth/msal';
import { useLocation, Navigate } from 'react-router-dom';
import { isAuthenticated, isTokenExpired } from './tokenUtils';
import { toast } from 'react-toastify';
import { usePermission } from './PermissionContext';
import { all_routes } from '../../router/all_routes';
import LoadingSpinner from '../../InitialPage/Sidebar/LoadingSpinner';

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const appAuthenticated = isAuthenticated();
  const { instance, accounts } = useMsal();
  const { me, status, checkRoute, landingRoute } = usePermission();
  const account = useAccount(accounts && accounts.length > 0 ? accounts[0] : null);

  useEffect(() => {
    // Check for token expiration on route change for authenticated users
    if (appAuthenticated) {
      // Double-check token expiration when navigating between protected routes
      if (isTokenExpired()) {
        // Clear all authentication data
        localStorage.removeItem('LoginInfo');
        localStorage.removeItem('UserName');
        localStorage.removeItem('UserId');
        localStorage.removeItem('RoleId');
        localStorage.removeItem('Token');
        localStorage.removeItem('Company');

        // Show a message about session expiration
        toast.error('Your session has expired. Please log in again.', {
          position: 'top-right',
          autoClose: 5000,
        });
      }
    }
  }, [appAuthenticated, location.pathname]);

  // Allow access if either the app has a local token OR MSAL account exists
  console.log('ProtectedRoute: Access Check', {
    appAuthenticated,
    hasMsalAccount: !!account,
    totalAccounts: accounts ? accounts.length : 0,
    loginInfo: localStorage.getItem('LoginInfo') ? 'Present' : 'Missing',
    token: localStorage.getItem('Token') ? 'Present' : 'Missing'
  });

  if (!appAuthenticated && !account) {
    console.warn('ProtectedRoute: Denying access, redirecting to login');
    instance.loginRedirect({ ...loginRequest })
    return null;
  }

  // Page access: route patterns from Access Control (supports '/cell/:id' style routes).
  // The child route is matched against the server snapshot; the API still checks every call.
  if (!me) {
    // 'idle' without an MSAL account means /me can never load (legacy local-token session).
    if (status === 'error' || (status === 'idle' && !account)) {
      return <Navigate to={all_routes.noaccess} replace state={{ reason: 'error' }} />;
    }
    return <LoadingSpinner />;
  }

  const { isProtected, allowed } = checkRoute(location.pathname);
  if (isProtected && !allowed) {
    return <DeniedRedirect landingRoute={landingRoute} pathname={location.pathname} />;
  }

  return children;
};

// Redirect once to the user's landing page (or the no-access page) — never back to the same route.
const DeniedRedirect = ({ landingRoute, pathname }) => {
  useEffect(() => {
    toast.error('You do not have access to this page.', { position: 'top-right', autoClose: 3000 });
  }, [pathname]);

  const target = landingRoute && landingRoute !== pathname ? landingRoute : all_routes.noaccess;
  return <Navigate to={target} replace />;
};

export default ProtectedRoute;